/**
 * 场景编排 API
 */
import { get, post, put, del, getPage } from '@/utils/request'
import type { ScenarioModule, Scenario, ScenarioStep } from '@/types'

// ============ 场景模块 ============

/** 场景模块树 */
export const getModuleTree = (projectId: number) =>
  get<ScenarioModule[]>('/scenarios/modules/tree', { project_id: projectId })

/** 创建场景模块 */
export const createModule = (data: { project_id: number; parent_id?: number | null; name: string; sort?: number }) =>
  post<ScenarioModule>('/scenarios/modules', data)

/** 更新场景模块 */
export const updateModule = (id: number, data: { name: string }) =>
  put<ScenarioModule>(`/scenarios/modules/${id}`, data)

/** 删除场景模块 */
export const deleteModule = (id: number) =>
  del(`/scenarios/modules/${id}`)

// ============ 场景 ============

/** 场景列表 */
export const listScenarios = (params: {
  project_id: number
  module_id?: number
  page?: number
  page_size?: number
  keyword?: string
}) => getPage<Scenario>('/scenarios', params)

/** 场景详情 */
export const getScenario = (id: number) =>
  get<Scenario>(`/scenarios/${id}`)

/** 创建场景 */
export const createScenario = (data: {
  project_id: number
  module_id?: number | null
  name: string
  description?: string
  environment?: string
  creator_id: number
}) => post<Scenario>('/scenarios', data)

/** 更新场景 */
export const updateScenario = (id: number, data: Partial<Scenario>) =>
  put<Scenario>(`/scenarios/${id}`, data)

/** 删除场景 */
export const deleteScenario = (id: number) =>
  del(`/scenarios/${id}`)

// ============ 场景步骤 ============

/** 场景步骤列表 */
export const listSteps = (scenarioId: number) =>
  get<ScenarioStep[]>(`/scenarios/${scenarioId}/steps`)

/** 创建场景步骤 */
export const createStep = (scenarioId: number, data: {
  name?: string
  step_type: number
  sort?: number
  parent_id?: number | null
}) => post<ScenarioStep>(`/scenarios/${scenarioId}/steps`, { ...data, scenario_id: scenarioId })

/** 更新场景步骤 */
export const updateStep = (scenarioId: number, stepId: number, data: Partial<ScenarioStep>) =>
  put<ScenarioStep>(`/scenarios/${scenarioId}/steps/${stepId}`, data)

/** 删除场景步骤 */
export const deleteStep = (scenarioId: number, stepId: number) =>
  del(`/scenarios/${scenarioId}/steps/${stepId}`)

/** 步骤排序 */
export const reorderSteps = (scenarioId: number, stepIds: number[]) =>
  put(`/scenarios/${scenarioId}/steps/reorder`, { step_ids: stepIds })

/** 移动步骤（移入/移出控制步骤作用域并调整排序） */
export const moveStep = (scenarioId: number, stepId: number, data: { parent_id: number | null; sort: number }) =>
  post<ScenarioStep>(`/scenarios/${scenarioId}/steps/${stepId}/move`, data)

/** 获取步骤详情 */
export const getStepDetail = (scenarioId: number, stepId: number) =>
  get<{ step_type: number; [key: string]: any }>(`/scenarios/${scenarioId}/steps/${stepId}/details`)

/** 创建步骤详情 */
export const createStepDetail = (scenarioId: number, stepId: number, data: Record<string, any>) =>
  post<{ step_type: number; [key: string]: any }>(`/scenarios/${scenarioId}/steps/${stepId}/details`, data)

/** 更新步骤详情 */
export const updateStepDetail = (scenarioId: number, stepId: number, data: Record<string, any>) =>
  put<{ step_type: number; [key: string]: any }>(`/scenarios/${scenarioId}/steps/${stepId}/details`, data)
